import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const timelineData = [
  {
    year: "1969",
    content:
      "> Kmart Australia opens its first store in Burwood, Victoria.\n > An estimated 40,000 people pass through the checkouts, taking over $97,000.",
    color: "#b7c100",
  },
  {
    year: "1978",
    content:
      "> Kresge exchanges a 51% stake in Kmart Australia for a 20% stake in Coles.",
    color: "#9e9e9e",
  },
  {
    year: "1988",
    content:
      "> Kmart expands to New Zealand and opens its first store in Auckland",
    color: "#d66a1f",
  },
  {
    year: "1994",
    content:
      "> Coles buys back all shares held by Kresge.\n > A long-term licensing agreement between Sears allows Wesfarmers to use the Kmart name in Australia and New Zealand.",
    color: "#2e7d32",
  },
  {
    year: "2006",
    content: "> Kmart merges with Coles Group.",
    color: "#7d3c2e",
  },
  {
    year: "2007",
    content:
      "> Wesfarmers takes complete control of Coles Group, which includes Kmart and Target.",
    color: "#b71a8d",
  },
  {
    year: "2008",
    content:
      "> Wesfarmers decides to invest $300 million in Kmart over the next five years.",
    color: "#3813c1",
  },
  {
    year: "2010",
    content: "> The year saw a large increase in Kmart EBIT by 74%.",
    color: "#0cbcb0",
  },
  {
    year: "2012",
    content:
      "> Australia's first multi-level Kmart opens in Adelaide's Rundle Mall",
    color: "#d8d516",
  },
  {
    year: "2017",
    content:
      "> Wesfarmers purchases the Kmart brand name for use in Australia and New Zealand for $100 million outright, ending the licensing agreement.",
    color: "#df152d",
  },
  {
    year: "2018",
    content: "> Kmart and Target merge to form Kmart Group.",
    color: "#1952cc",
  },
  { year: "2019", content: "> Catch.com.au is acquired.", color: "#0b7008" },
  {
    year: "2023",
    content: "> Kmart and Target merges to form one business, two brands",
    color: "#b789d2",
  },
  {
    year: "2024",
    content: "> KAS Group Asia(KGA) transitions to Anko Sourcing",
    color: "#6bbeb9",
  },
];

export interface JourneyTimelineRef {
  scrollPrev: () => void;
  scrollNext: () => void;
}

const JourneyTimeline = forwardRef<JourneyTimelineRef>((_props, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentIndexRef = useRef(0);

  const getStepWidth = () => {
    const container = containerRef.current;
    if (!container) return 0;
    const firstItem = container.firstChild as HTMLElement;
    if (!firstItem) return 0;
    return firstItem.getBoundingClientRect().width;
  };

  useImperativeHandle(ref, () => ({
    scrollPrev: () => {
      const container = containerRef.current;
      if (!container) return;
      const stepWidth = getStepWidth();
      currentIndexRef.current = Math.max(0, currentIndexRef.current - 1);
      container.scrollTo({
        left: currentIndexRef.current * stepWidth,
        behavior: "smooth",
      });
    },
    scrollNext: () => {
      const container = containerRef.current;
      if (!container) return;
      const stepWidth = getStepWidth();
      currentIndexRef.current++;
      if (currentIndexRef.current < timelineData.length) {
        container.scrollTo({
          left: currentIndexRef.current * stepWidth,
          behavior: "smooth",
        });
      } else {
        container.scrollTo({ left: 0, behavior: "auto" });
        currentIndexRef.current = 0;
      }
    },
  }));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const totalItems = timelineData.length;

    const interval = setInterval(() => {
      const stepWidth = getStepWidth();

      currentIndexRef.current++;
      if (currentIndexRef.current < totalItems) {
        container.scrollTo({
          left: currentIndexRef.current * stepWidth,
          behavior: "smooth",
        });
      } else {
        container.scrollTo({ left: 0, behavior: "auto" });
        currentIndexRef.current = 0;
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
      {/* Background Line */}
      <Box
        sx={{
          position: "absolute",
          top: "56px",
          left: 0,
          width: "100%",
          height: "2px",
          bgcolor: "common.black",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          overflowX: "hidden",
          scrollBehavior: "smooth",
          width: "100%",
        }}
      >
        {/* Double the data for infinite loop feel */}
        {[...timelineData, ...timelineData].map((item, index) => (
          <Box
            key={index}
            sx={{
              // RESPONSIVE WIDTHS:
              // Mobile: 1 card (100%)
              // Tablet and larger: 3 cards (33.33%)
              flex: { xs: "0 0 100%", sm: "0 0 33.33%", md: "0 0 33.33%" },
              width: { xs: "100%", sm: "33.33%", md: "33.33%" },
              flexShrink: 0,
              boxSizing: "border-box",
              // Horizontal padding to separate the cards visually
              px: { xs: 4, sm: 3, md: 4 },
            }}
          >
            {/* Year Label */}
            <Box
              sx={{
                height: "56px",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                pb: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "0.75rem", lg: "1.7rem" },
                  fontWeight: 500,
                }}
              >
                {item.year}
              </Typography>
            </Box>

            {/* Timeline Dot */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: "-7px",
                mb: "7px",
                position: "relative",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 14,
                  borderRadius: "20px",
                  bgcolor: item.color,
                }}
              />
            </Box>

            {/* Content Text */}
            <Box sx={{ pt: 3 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1rem", sm: "0.95rem", md: "1rem" },
                  lineHeight: 1.6,
                  maxWidth: { xs: "100%", sm: "90%", md: "280px" },
                  mx: "auto",
                  textAlign: "center",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  fontFamily: "inherit",
                }}
              >
                {item.content.replace(/>/g, "").trim()}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export default JourneyTimeline;
